import {
  alpha,
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  Psychology,
  RadioButtonUnchecked,
  RemoveCircleOutline,
  Save,
} from "@mui/icons-material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { getChildDevelopment, getChildren, updateChildDevelopment } from "apis";
import { formatChildAge, stringAvatar } from "utils";
import dayjs from "dayjs";

interface ChildState {
  id: string | number;
  firstName: string;
  lastName: string;
  photo?: string;
  dateOfBirth?: string;
}

interface Objective {
  matterId: number;
  code: string;
  ageBandId: number;
  description: string;
  status: number;
  assessedAt: string | null;
}

interface AgeBand {
  id: number;
  code: string;
  sortOrder: number;
  label: string;
  minMonths: number;
  maxMonths: number | null;
}

interface Area {
  areaId: number;
  code: string;
  label: string;
  total: number;
  assessed: number;
  achieved: number;
  objectives: Objective[];
  ageBands: AgeBand[];
}

type StatusFilter = "all" | "not_observed" | "emerging" | "achieved";

const statusLabel = (status: number, t: (key: string) => string) => {
  switch (status) {
    case 2:
      return t("grading.achieved");
    case 1:
      return t("grading.working_towards");
    default:
      return t("grading.not_observed");
  }
};

const STATUS_SEGMENTS = [
  { value: 0, Icon: RadioButtonUnchecked, palette: "default" as const },
  { value: 1, Icon: RemoveCircleOutline, palette: "warning" as const },
  { value: 2, Icon: CheckCircle, palette: "success" as const },
];

const ProgressRing: React.FC<{
  assessed: number;
  total: number;
  achieved: number;
  size?: number;
}> = ({ assessed, total, achieved, size = 56 }) => {
  const theme = useTheme();
  const pct = total > 0 ? (assessed / total) * 100 : 0;
  return (
    <Box
      sx={{
        position: "relative",
        display: "inline-flex",
        width: size,
        height: size,
      }}
    >
      <CircularProgress
        variant="determinate"
        value={100}
        size={size}
        thickness={4}
        sx={{
          color: alpha(theme.palette.action.disabled, 0.15),
          position: "absolute",
        }}
      />
      <CircularProgress
        variant="determinate"
        value={pct}
        size={size}
        thickness={4}
        sx={{ color: achieved > 0 ? "success.main" : "primary.main" }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ fontSize: 11 }}
        >
          {assessed}/{total}
        </Typography>
      </Box>
    </Box>
  );
};

interface AreaTileProps {
  area: Area;
  objectives: Objective[];
  isSelected: boolean;
  changes: Map<number, number>;
  onSelect: (areaId: number) => void;
}

const AreaTile = React.memo(
  ({ area, objectives, isSelected, changes, onSelect }: AreaTileProps) => {
    const theme = useTheme();

    const getEffective = (obj: Objective) =>
      changes.has(obj.matterId) ? changes.get(obj.matterId)! : obj.status;

    const total = objectives.length;
    const assessed = objectives.reduce(
      (a, o) => a + (getEffective(o) > 0 ? 1 : 0),
      0,
    );
    const achieved = objectives.reduce(
      (a, o) => a + (getEffective(o) === 2 ? 1 : 0),
      0,
    );
    const emerging = assessed - achieved;

    return (
      <Card
        variant="outlined"
        sx={{
          height: "100%",
          borderRadius: 2,
          borderColor: isSelected ? "primary.main" : "divider",
          borderWidth: isSelected ? 2 : 1,
          transition: "border-color 0.15s, box-shadow 0.15s",
          boxShadow: isSelected
            ? `0 0 0 1px ${theme.palette.primary.main}`
            : "none",
        }}
      >
        <CardActionArea
          onClick={() => onSelect(area.areaId)}
          sx={{
            p: 1.5,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: 1,
          }}
        >
          <ProgressRing
            assessed={assessed}
            total={total}
            achieved={achieved}
            size={48}
          />
          <Typography
            variant="caption"
            fontWeight={600}
            align="center"
            sx={{
              lineHeight: 1.3,
              minHeight: 32,
              flex: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            {area.label}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              minHeight: 20,
              alignItems: "center",
            }}
          >
            {achieved > 0 && (
              <Chip
                label={`${achieved}✓`}
                size="small"
                color="success"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
            {emerging > 0 && (
              <Chip
                label={`${emerging}~`}
                size="small"
                color="warning"
                sx={{ height: 20, fontSize: 11 }}
              />
            )}
          </Box>
        </CardActionArea>
      </Card>
    );
  },
);

interface ObjectivesPanelProps {
  area: Area;
  objectives: Objective[];
  changes: Map<number, number>;
  onSetStatus: (matterId: number, status: number) => void;
  t: (key: string, options?: { count?: number }) => string;
}

const ObjectivesPanel = React.memo(
  ({ area, objectives, changes, onSetStatus, t }: ObjectivesPanelProps) => {
    const theme = useTheme();

    const getEffective = (obj: Objective) =>
      changes.has(obj.matterId) ? changes.get(obj.matterId)! : obj.status;

    return (
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          borderColor: "primary.main",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: alpha(theme.palette.primary.main, 0.04),
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography fontWeight={700} variant="body2">
            {area.label}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {t("grading.objective_count", { count: objectives.length })}
          </Typography>
        </Box>
        <Divider />
        {objectives.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("grading.no_objectives")}
            </Typography>
          </Box>
        ) : (
          objectives.map((obj) => {
            const effectiveStatus = getEffective(obj);
            const isChanged = changes.has(obj.matterId);
            return (
              <Box
                key={obj.matterId}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderTop: 1,
                  borderColor: "divider",
                  bgcolor: isChanged
                    ? alpha(theme.palette.warning.main, 0.04)
                    : undefined,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
                    {obj.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 0.25,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {obj.code}
                    </Typography>
                    {obj.assessedAt && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontStyle: "italic" }}
                      >
                        · {t("grading.last_assessed")}{" "}
                        {dayjs(obj.assessedAt).format("D MMM YYYY")}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <ToggleButtonGroup
                  value={effectiveStatus}
                  exclusive
                  size="small"
                  onChange={(_, v) => {
                    if (v !== null) onSetStatus(obj.matterId, v);
                  }}
                  sx={{
                    flexShrink: 0,
                    "& .MuiToggleButton-root": {
                      border: 0,
                      width: 36,
                      height: 36,
                      color: "text.disabled",
                    },
                  }}
                >
                  {STATUS_SEGMENTS.map(({ value, Icon, palette }) => (
                    <ToggleButton
                      key={value}
                      value={value}
                      aria-label={statusLabel(value, t)}
                      sx={{
                        "&.Mui-selected": {
                          color:
                            palette === "success"
                              ? "success.main"
                              : palette === "warning"
                                ? "warning.main"
                                : "text.primary",
                          bgcolor:
                            palette === "success"
                              ? alpha(theme.palette.success.main, 0.12)
                              : palette === "warning"
                                ? alpha(theme.palette.warning.main, 0.12)
                                : alpha(theme.palette.action.selected, 0.16),
                          "&:hover": {
                            bgcolor:
                              palette === "success"
                                ? alpha(theme.palette.success.main, 0.18)
                                : palette === "warning"
                                  ? alpha(theme.palette.warning.main, 0.18)
                                  : alpha(theme.palette.action.selected, 0.22),
                          },
                        },
                      }}
                    >
                      <Icon fontSize="small" />
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Box>
            );
          })
        )}
      </Card>
    );
  },
);

const ChildDevelopment: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const { childId } = useParams<{ childId: string }>();
  const [searchParams] = useSearchParams();
  const editingEventId = useMemo(() => {
    const v = searchParams.get("eventId");
    if (!v) return null;
    const n = Number(v);
    return Number.isInteger(n) && n > 0 ? n : null;
  }, [searchParams]);
  const { enqueueSnackbar } = useSnackbar();

  const [child, setChild] = useState<ChildState | null>(null);
  const [areas, setAreas] = useState<Area[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState<Map<number, number>>(new Map());
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [ageBandId, setAgeBandId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [selectedArea, setSelectedArea] = useState<number | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);

  const fetchData = useCallback(
    (opts?: { silent?: boolean }) => {
      if (!childId) return;
      if (!opts?.silent) setLoading(true);
      Promise.all([getChildDevelopment(childId), getChildren()])
        .then(([devData, children]) => {
          setAreas(devData || []);
          const found = (children || []).find(
            (c: { id: string | number }) => String(c.id) === String(childId),
          );
          setChild(found || null);
          setErrorKey(null);
        })
        .catch(() => {
          setErrorKey("errors.load_failed");
        })
        .finally(() => setLoading(false));
    },
    [childId],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Server-side localised payload (area labels, matter descriptions) needs
  // to refetch when the user switches language. Silent refresh keeps the
  // existing layout in place; pending unsaved edits in `changes` survive.
  useEffect(() => {
    if (areas.length > 0) fetchData({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const ageBands: AgeBand[] = areas[0]?.ageBands ?? [];

  const suggestedAgeBandId = useMemo(() => {
    if (!child?.dateOfBirth || ageBands.length === 0) return null;
    const months = dayjs().diff(dayjs(child.dateOfBirth), "month");
    const found = ageBands.find(
      (b) =>
        months >= b.minMonths && (b.maxMonths === null || months < b.maxMonths),
    );
    return found?.id ?? ageBands[ageBands.length - 1].id;
  }, [child?.dateOfBirth, ageBands]);

  useEffect(() => {
    if (suggestedAgeBandId !== null) setAgeBandId(suggestedAgeBandId);
  }, [suggestedAgeBandId]);

  const childAge = useMemo(
    () => formatChildAge(child?.dateOfBirth, t),
    [child?.dateOfBirth, t],
  );

  const setStatus = useCallback((matterId: number, status: number) => {
    setChanges((prev) => {
      const updated = new Map(prev);
      updated.set(matterId, status);
      return updated;
    });
  }, []);

  const getEffectiveStatus = useCallback(
    (obj: Objective) =>
      changes.has(obj.matterId) ? changes.get(obj.matterId)! : obj.status,
    [changes],
  );

  const hasChanges = changes.size > 0;

  useEffect(() => {
    if (!hasChanges) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasChanges]);

  const handleBack = useCallback(() => {
    if (hasChanges) {
      setDiscardOpen(true);
    } else {
      navigate("/child");
    }
  }, [hasChanges, navigate]);

  const handleDiscard = useCallback(() => {
    setDiscardOpen(false);
    setChanges(new Map());
    navigate("/child");
  }, [navigate]);

  const handleSave = async () => {
    if (!hasChanges || !childId) return;
    setSaving(true);
    try {
      const statuses = Array.from(changes.entries()).map(
        ([matterId, status]) => ({ matterId, status }),
      );
      await updateChildDevelopment(childId, {
        statuses,
        ...(editingEventId !== null ? { eventId: editingEventId } : {}),
      });
      enqueueSnackbar(t("grading.saved"), { variant: "success" });
      setChanges(new Map());
      fetchData({ silent: true });
    } catch {
      enqueueSnackbar(t("errors.save_failed"), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  const getFilteredObjectives = useCallback(
    (area: Area) => {
      if (ageBandId === null) return [];
      let objs = area.objectives.filter((o) => o.ageBandId === ageBandId);
      if (statusFilter !== "all") {
        objs = objs.filter((o) => {
          const s = getEffectiveStatus(o);
          switch (statusFilter) {
            case "not_observed":
              return s === 0;
            case "emerging":
              return s === 1;
            case "achieved":
              return s === 2;
            default:
              return true;
          }
        });
      }
      return objs;
    },
    [ageBandId, statusFilter, getEffectiveStatus],
  );

  const getBandObjectives = useCallback(
    (area: Area) =>
      ageBandId === null
        ? []
        : area.objectives.filter((o) => o.ageBandId === ageBandId),
    [ageBandId],
  );

  const tabSummary = useMemo(() => {
    let total = 0;
    let assessed = 0;
    let achieved = 0;
    if (ageBandId !== null) {
      for (const area of areas) {
        for (const o of area.objectives) {
          if (o.ageBandId !== ageBandId) continue;
          total++;
          const s = getEffectiveStatus(o);
          if (s > 0) assessed++;
          if (s === 2) achieved++;
        }
      }
    }
    return { total, assessed, achieved, notAssessed: total - assessed };
  }, [areas, ageBandId, getEffectiveStatus]);

  const selectedAreaSummary = useMemo(() => {
    let total = 0;
    let assessed = 0;
    let achieved = 0;
    if (ageBandId !== null && selectedArea !== null) {
      const area = areas.find((a) => a.areaId === selectedArea);
      if (area) {
        for (const o of area.objectives) {
          if (o.ageBandId !== ageBandId) continue;
          total++;
          const s = getEffectiveStatus(o);
          if (s > 0) assessed++;
          if (s === 2) achieved++;
        }
      }
    }
    return { total, assessed, achieved, notAssessed: total - assessed };
  }, [areas, ageBandId, selectedArea, getEffectiveStatus]);

  const handleSelectArea = useCallback((areaId: number) => {
    setSelectedArea((prev) => (prev === areaId ? null : areaId));
  }, []);

  useEffect(() => {
    if (areas.length === 0 || ageBandId === null) {
      setSelectedArea(null);
      return;
    }
    const firstWithMatters = areas.find((a) =>
      a.objectives.some((o) => o.ageBandId === ageBandId),
    );
    setSelectedArea(firstWithMatters?.areaId ?? null);
  }, [ageBandId, areas]);

  if (!childId) {
    return (
      <Container sx={{ py: 8, textAlign: "center" }}>
        <Typography color="text.secondary">{t("child.not_found")}</Typography>
      </Container>
    );
  }

  const selectedAreaObj = areas.find((a) => a.areaId === selectedArea);

  const filterChip = (
    filter: StatusFilter,
    label: string,
    count: number,
    color?: "success" | "warning" | "default",
  ) => (
    <Chip
      label={`${label} (${count})`}
      size="small"
      variant={statusFilter === filter ? "filled" : "outlined"}
      color={statusFilter === filter ? color || "primary" : "default"}
      onClick={() => setStatusFilter(statusFilter === filter ? "all" : filter)}
      sx={{
        cursor: "pointer",
        fontWeight: statusFilter === filter ? 600 : 400,
      }}
    />
  );

  return (
    <>
      <Toolbar
        sx={{
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          gap: 1.5,
        }}
      >
        <IconButton
          onClick={handleBack}
          size="small"
          sx={{
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            '[dir="rtl"] &': { transform: "scaleX(-1)" },
          }}
        >
          <ArrowBack fontSize="small" />
        </IconButton>
        <Psychology sx={{ color: "primary.main", fontSize: 22 }} />
        <Typography variant="h6" fontWeight={600}>
          {t("development.label")}
        </Typography>
        {child && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Avatar
              src={child.photo}
              {...(!child.photo && child.firstName
                ? stringAvatar(`${child.firstName} ${child.lastName}`, {
                    width: 28,
                    height: 28,
                    fontSize: 11,
                  })
                : { sx: { width: 28, height: 28, fontSize: 11 } })}
            />
            <Typography variant="body2" color="text.secondary">
              {child.firstName} {child.lastName}
              {childAge ? ` (${childAge})` : ""}
            </Typography>
          </Box>
        )}
        <Box sx={{ ml: "auto" }}>
          <Button
            variant="contained"
            size="small"
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : <Save />
            }
            onClick={handleSave}
            disabled={!hasChanges || saving}
            sx={{ textTransform: "none", borderRadius: 2 }}
          >
            {t("buttons.save")}
          </Button>
        </Box>
      </Toolbar>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : errorKey ? (
        <Container maxWidth="md" sx={{ py: 8, textAlign: "center" }}>
          <Typography color="error" gutterBottom>
            {t(errorKey)}
          </Typography>
          <Button onClick={() => fetchData()} variant="outlined">
            {t("buttons.retry")}
          </Button>
        </Container>
      ) : (
        <Container maxWidth="md" sx={{ py: 2 }}>
          <Stack spacing={2}>
            {ageBands.length > 0 && ageBandId !== null && (
              <ToggleButtonGroup
                value={ageBandId}
                exclusive
                onChange={(_, v) => {
                  if (v === null || v === ageBandId) return;
                  if (hasChanges) {
                    enqueueSnackbar(t("grading.save_before_band_switch"), {
                      variant: "warning",
                    });
                    return;
                  }
                  setAgeBandId(v);
                }}
                size="small"
                sx={{
                  width: "100%",
                  bgcolor: alpha(theme.palette.action.hover, 0.04),
                  borderRadius: 2,
                  p: 0.5,
                  "& .MuiToggleButton-root": {
                    flex: 1,
                    minWidth: 0,
                    border: "none",
                    borderRadius: "6px !important",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 12,
                    py: 0.75,
                    px: 0.75,
                    color: "text.secondary",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    "&.Mui-selected": {
                      bgcolor: "background.paper",
                      color: "text.primary",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                      "&:hover": { bgcolor: "background.paper" },
                    },
                  },
                }}
              >
                {ageBands.map((b) => (
                  <ToggleButton key={b.id} value={b.id}>
                    {b.label}
                    {suggestedAgeBandId === b.id && (
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "success.main",
                          ml: 0.75,
                        }}
                      />
                    )}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            )}

            {ageBandId !== null && tabSummary.total > 0 && (
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  border: 1,
                  borderColor: "divider",
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  flexWrap: "wrap",
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  {ageBands.find((b) => b.id === ageBandId)?.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ·
                </Typography>
                <Typography variant="body2">
                  {tabSummary.assessed}/{tabSummary.total}{" "}
                  {t("grading.assessed")}
                </Typography>
                {tabSummary.achieved > 0 && (
                  <Chip
                    label={`${tabSummary.achieved} ✓`}
                    size="small"
                    color="success"
                    sx={{ height: 22, fontWeight: 600 }}
                  />
                )}
                {tabSummary.assessed - tabSummary.achieved > 0 && (
                  <Chip
                    label={`${tabSummary.assessed - tabSummary.achieved} ~`}
                    size="small"
                    color="warning"
                    sx={{ height: 22, fontWeight: 600 }}
                  />
                )}
              </Box>
            )}

            <Box>
              <Typography
                variant="overline"
                fontWeight={700}
                sx={{
                  letterSpacing: 1.5,
                  mb: 1,
                  display: "block",
                  color: theme.palette.primary.main,
                }}
              >
                {t("grading.development_matters")}
              </Typography>
              <Grid container spacing={1.5} alignItems="stretch">
                {areas.map((area) => (
                  <Grid
                    item
                    xs={4}
                    sm={4}
                    md
                    key={area.areaId}
                    sx={{ display: "flex" }}
                  >
                    <Box sx={{ width: "100%" }}>
                      <AreaTile
                        area={area}
                        objectives={getBandObjectives(area)}
                        isSelected={selectedArea === area.areaId}
                        changes={changes}
                        onSelect={handleSelectArea}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Collapse in={selectedArea !== null} unmountOnExit>
              {selectedAreaObj && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      alignItems: "center",
                      mb: 1.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {t("grading.filter")}:
                    </Typography>
                    {filterChip(
                      "not_observed",
                      t("grading.not_observed"),
                      selectedAreaSummary.notAssessed,
                    )}
                    {filterChip(
                      "emerging",
                      t("grading.working_towards"),
                      selectedAreaSummary.assessed -
                        selectedAreaSummary.achieved,
                      "warning",
                    )}
                    {filterChip(
                      "achieved",
                      t("grading.achieved"),
                      selectedAreaSummary.achieved,
                      "success",
                    )}
                  </Box>
                  <ObjectivesPanel
                    area={selectedAreaObj}
                    objectives={getFilteredObjectives(selectedAreaObj)}
                    changes={changes}
                    onSetStatus={setStatus}
                    t={t}
                  />
                </>
              )}
            </Collapse>
          </Stack>
        </Container>
      )}

      <Dialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("unsaved.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("unsaved.body")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscardOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button onClick={handleDiscard} color="error" variant="contained">
            {t("buttons.discard")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ChildDevelopment;
